/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { Link } from 'react-router-dom'
import cx from 'classnames'

import type { EuiLinkColor } from '@elastic/eui'

import type { CommonProps } from '@modules/ui-types'

import type { LinkProps } from 'react-router-dom'
import type { ComponentType, FunctionComponent, ReactNode } from 'react'
import type { StrictOmit } from '../../ts-essentials'

import './cuiLink.scss'

type AnchorProps = {
  children?: ReactNode
  to: LinkProps['to']
  className?: string
}

interface CuiRouterLinkProps extends CommonProps, LinkProps {
  component: ComponentType<AnchorProps>
  color?: EuiLinkColor | 'inherit'
}

const CuiLinkCustom: FunctionComponent<CuiRouterLinkProps> = ({
  component: LinkTag,
  children,
  className,
  color = `primary`,
  ...other
}) => {
  const inherit = color === 'inherit'

  const classes = cx(
    `euiLink`,
    {
      [`euiLink--${color}`]: !inherit,
      'cuiLink--inheritColor': inherit,
    },
    className,
  )

  return (
    <LinkTag className={classes} {...other}>
      {children}
    </LinkTag>
  )
}

type CuiLinkProps = StrictOmit<CuiRouterLinkProps, 'component'>

/**
 * @deprecated in favor of CuiRouterLink
 * @motivation
 *  - This component does not use EUI components but only partially copies its styles.
 *  - Additionally, we already have @modules/cui/RouterButton components that provides the same functionality
 */
export const CuiLink: FunctionComponent<CuiLinkProps> = ({ ...props }) => (
  <CuiLinkCustom component={Link} {...props} />
)
